import React from "react";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Slider } from "@/components/ui/slider";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "@/components/ui/select";
import {
  Upload, Download, Layers, ShirtIcon, Flame, ImageIcon, Loader2, RotateCcw, Sparkles,
  Files, ShirtIcon as Shirt, Palette, Save, Package,
} from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import {
  HALFTONE_STYLES, DEFAULT_HALFTONE_PARAMS,
  renderHalftone, renderUnderbase, downloadCanvas, loadImageFromFile,
  renderCmykSeparation, renderMockup, loadImageFromDataUrl,
} from "@/lib/halftone";
import { useAuth } from "@/context/AuthContext";
import { UserAPI, AiAPI, ProjectsAPI, apiErrorMessage } from "@/lib/api";
import JSZip from "jszip";

const FABRICS = [
  { id: "dark", label: "Dark shirt" },
  { id: "light", label: "Light shirt" },
  { id: "color", label: "Color shirt" },
];

// Simple mockup base — vector-drawn tee so we don't need external assets
function drawTeeMockup(color = "#111827") {
  const c = document.createElement("canvas");
  c.width = 900; c.height = 900;
  const ctx = c.getContext("2d");
  ctx.fillStyle = color;
  // Body
  ctx.beginPath();
  ctx.moveTo(180, 260);
  ctx.lineTo(320, 160);
  ctx.quadraticCurveTo(450, 100, 580, 160);
  ctx.lineTo(720, 260);
  ctx.lineTo(660, 340);
  ctx.lineTo(660, 820);
  ctx.quadraticCurveTo(450, 850, 240, 820);
  ctx.lineTo(240, 340);
  ctx.closePath();
  ctx.fill();
  // Collar
  ctx.beginPath();
  ctx.moveTo(390, 200);
  ctx.quadraticCurveTo(450, 240, 510, 200);
  ctx.strokeStyle = "rgba(0,0,0,0.25)";
  ctx.lineWidth = 6;
  ctx.stroke();
  // Fabric texture (soft dots)
  ctx.globalAlpha = 0.06;
  for (let y = 260; y < 820; y += 6) {
    for (let x = 240; x < 660; x += 6) {
      ctx.fillStyle = "#ffffff";
      ctx.fillRect(x, y, 1, 1);
    }
  }
  ctx.globalAlpha = 1;
  return c;
}

function fabricDefaults(fabric) {
  if (fabric === "dark") return { lpi: 30, angle: 22.5, underbase: true, fgColor: "#ffffff", bgTransparent: true };
  if (fabric === "light") return { lpi: 45, angle: 22.5, underbase: false, fgColor: "#000000", bgTransparent: true };
  return { lpi: 35, angle: 22.5, underbase: true, fgColor: "#000000", bgTransparent: true };
}

export default function HalftoneStudioPage() {
  const { user, usage, refreshUsage } = useAuth();
  const { toast } = useToast();
  const fileRef = React.useRef(null);
  const previewRef = React.useRef(null);
  const underbaseRef = React.useRef(null);
  const [img, setImg] = React.useState(null);
  const [fileName, setFileName] = React.useState("");
  const [style, setStyle] = React.useState("am-round");
  const [params, setParams] = React.useState({ ...DEFAULT_HALFTONE_PARAMS });
  const [busy, setBusy] = React.useState(false);
  const [aiBusy, setAiBusy] = React.useState(false);
  const [mode, setMode] = React.useState("single"); // single | batch | cmyk | mockup

  // Batch state
  const [batchFiles, setBatchFiles] = React.useState([]); // { file, name, imgEl, resultCanvas, done }

  // CMYK state
  const cmykRefs = { C: React.useRef(null), M: React.useRef(null), Y: React.useRef(null), K: React.useRef(null), COMPOSITE: React.useRef(null) };
  const [cmykReady, setCmykReady] = React.useState(false);

  // Mockup state
  const mockupRef = React.useRef(null);
  const [teeColor, setTeeColor] = React.useState("#111827");

  const rerender = React.useCallback(() => {
    if (!img || !previewRef.current) return;
    renderHalftone(img, previewRef.current, params, style);
    if (params.underbase && underbaseRef.current) {
      renderUnderbase(img, underbaseRef.current, params);
    }
  }, [img, params, style]);

  React.useEffect(() => { if (mode === "single") rerender(); }, [rerender, mode]);

  const onFile = async (file) => {
    if (!file) return;
    setFileName(file.name);
    const image = await loadImageFromFile(file);
    setImg(image);
  };

  const setFabric = (id) => {
    const d = fabricDefaults(id);
    setParams((p) => ({ ...p, fabric: id, ...d }));
  };

  const guardExport = async () => {
    if (!user) {
      toast({ title: "Sign in to export", description: "Create a free account to save your art." });
      return false;
    }
    try {
      await UserAPI.trackExport("halftone", style, fileName || "halftone.png");
      await refreshUsage();
      return true;
    } catch (err) {
      toast({ title: "Export limit reached", description: apiErrorMessage(err, "Upgrade to keep exporting"), variant: "destructive" });
      return false;
    }
  };

  const exportColor = async () => {
    if (!previewRef.current) return;
    setBusy(true);
    try {
      const ok = await guardExport();
      if (!ok) return;
      downloadCanvas(previewRef.current, `${(fileName || "art").replace(/\.[^.]+$/, "")}_halftone_${style}_300dpi.png`);
      toast({ title: "Downloaded color layer" });
    } finally { setBusy(false); }
  };

  const exportUnderbase = async () => {
    if (!underbaseRef.current) return;
    setBusy(true);
    try {
      const ok = await guardExport();
      if (!ok) return;
      downloadCanvas(underbaseRef.current, `${(fileName || "art").replace(/\.[^.]+$/, "")}_underbase_300dpi.png`);
      toast({ title: "Downloaded underbase" });
    } finally { setBusy(false); }
  };

  const saveProject = async () => {
    if (!previewRef.current || !user) {
      toast({ title: "Sign in to save projects", variant: "destructive" });
      return;
    }
    try {
      const thumbCanvas = document.createElement("canvas");
      thumbCanvas.width = 240; thumbCanvas.height = 240;
      thumbCanvas.getContext("2d").drawImage(previewRef.current, 0, 0, 240, 240);
      const thumb = thumbCanvas.toDataURL("image/png").split(",")[1];
      const exportB64 = previewRef.current.toDataURL("image/png").split(",")[1];
      await ProjectsAPI.save({
        name: fileName || `Halftone ${new Date().toLocaleString()}`,
        kind: "halftone", style,
        thumbnail_base64: thumb,
        export_base64: exportB64,
        params, filename: fileName,
      });
      toast({ title: "Saved to Projects", description: "Find it in your dashboard." });
    } catch (err) {
      toast({ title: "Couldn't save", description: apiErrorMessage(err), variant: "destructive" });
    }
  };

  const runDtfOptimize = async () => {
    if (!previewRef.current || !img) return;
    if (!(usage?.creator || usage?.plan === "pro_lifetime")) {
      toast({ title: "Creator plan required", description: "Auto DTF/DTG optimize is a Creator / All-Access feature.", variant: "destructive" });
      return;
    }
    setAiBusy(true);
    try {
      const canvas = document.createElement("canvas");
      const w = img.naturalWidth || img.width;
      const h = img.naturalHeight || img.height;
      canvas.width = w; canvas.height = h;
      canvas.getContext("2d").drawImage(img, 0, 0);
      const b64 = canvas.toDataURL("image/png").split(",")[1];
      const r = await AiAPI.dtfOptimize(b64);
      const dataUrl = `data:${r.data.mime_type};base64,${r.data.image_base64}`;
      const newImg = await loadImageFromDataUrl(dataUrl);
      setImg(newImg);
      toast({ title: "AI optimized for DTF" });
    } catch (err) {
      toast({ title: "Optimize failed", description: apiErrorMessage(err), variant: "destructive" });
    } finally { setAiBusy(false); }
  };

  // -------- Batch mode --------
  const onBatchFiles = async (files) => {
    const arr = Array.from(files || []).slice(0, 20);
    const loaded = await Promise.all(arr.map(async (f) => {
      const imgEl = await loadImageFromFile(f);
      return { file: f, name: f.name, imgEl, resultCanvas: null, done: false };
    }));
    setBatchFiles(loaded);
  };

  const runBatch = async () => {
    if (!batchFiles.length) return;
    setBusy(true);
    try {
      const updated = [];
      for (const b of batchFiles) {
        const c = document.createElement("canvas");
        renderHalftone(b.imgEl, c, params, style);
        updated.push({ ...b, resultCanvas: c, done: true });
        setBatchFiles([...updated, ...batchFiles.slice(updated.length)]);
      }
      setBatchFiles(updated);
      toast({ title: `Rendered ${updated.length} halftones`, description: "Click Download all to grab the zip." });
    } finally { setBusy(false); }
  };

  const downloadBatch = async () => {
    if (!batchFiles.some((b) => b.resultCanvas)) return;
    // Track export per file (free-tier gated)
    const ok = await guardExport();
    if (!ok && !usage?.pro) {
      toast({ title: "Free tier: 1 export at a time", description: "Upgrade for batch export.", variant: "destructive" });
      return;
    }
    // Zip using tiny inline zip writer (STORE method)
    const zip = new JSZip();
    for (const b of batchFiles) {
      if (!b.resultCanvas) continue;
      const blob = await new Promise((res) => b.resultCanvas.toBlob(res, "image/png"));
      zip.file(`${b.name.replace(/\.[^.]+$/, "")}_halftone_${style}.png`, blob);
    }
    const zipBlob = await zip.generateAsync({ type: "blob" });
    const url = URL.createObjectURL(zipBlob);
    const a = document.createElement("a");
    a.href = url; a.download = `furnace_batch_${Date.now()}.zip`; a.click();
    setTimeout(() => URL.revokeObjectURL(url), 3000);
  };

  // -------- CMYK sim process --------
  const runCmyk = async () => {
    if (!img) { toast({ title: "Upload an image first", variant: "destructive" }); return; }
    setBusy(true);
    try {
      const { channels, composite } = renderCmykSeparation(img, params, style);
      // Draw into refs
      for (const ch of ["C", "M", "Y", "K"]) {
        const ref = cmykRefs[ch];
        if (ref.current) {
          ref.current.width = channels[ch].width;
          ref.current.height = channels[ch].height;
          ref.current.getContext("2d").drawImage(channels[ch], 0, 0);
        }
      }
      if (cmykRefs.COMPOSITE.current) {
        cmykRefs.COMPOSITE.current.width = composite.width;
        cmykRefs.COMPOSITE.current.height = composite.height;
        cmykRefs.COMPOSITE.current.getContext("2d").drawImage(composite, 0, 0);
      }
      setCmykReady(true);
    } finally { setBusy(false); }
  };

  const downloadCmyk = async (ch) => {
    const ok = await guardExport();
    if (!ok) return;
    downloadCanvas(cmykRefs[ch].current, `${(fileName || "art").replace(/\.[^.]+$/, "")}_${ch}_${style}.png`);
  };

  // -------- Mockup --------
  const renderTeeMockup = React.useCallback(() => {
    if (!previewRef.current) return;
    const tee = drawTeeMockup(teeColor);
    const out = renderMockup(tee, previewRef.current, { x: 0.5, y: 0.55, scale: 0.36 });
    if (mockupRef.current) {
      mockupRef.current.width = out.width;
      mockupRef.current.height = out.height;
      mockupRef.current.getContext("2d").drawImage(out, 0, 0);
    }
  }, [teeColor]);

  React.useEffect(() => {
    if (mode === "mockup" && img) {
      // Ensure we've rendered a halftone into previewRef first
      const c = previewRef.current || document.createElement("canvas");
      if (!previewRef.current) previewRef.current = c;
      renderHalftone(img, c, params, style);
      renderTeeMockup();
    }
  }, [mode, img, params, style, renderTeeMockup]);

  return (
    <div className="grain">
      <Navbar />
      <section className="mx-auto max-w-7xl px-4 py-10 sm:px-6">
        <div className="flex flex-wrap items-end justify-between gap-4">
          <div>
            <span className="chip"><Layers className="h-3 w-3 text-[#4aa4ff]" /> Halftone Studio</span>
            <h1 className="display-font mt-3 text-3xl font-bold sm:text-4xl">Press-ready halftones in <span className="ember-text">seconds</span>.</h1>
            <p className="mt-1 text-sm text-muted-foreground">12 styles · smart underbase · fabric picker · CMYK sim process · batch · mockup</p>
          </div>
          <div className="flex items-center gap-2">
            <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Upload art</Button>
            <input ref={fileRef} type="file" accept="image/*" hidden onChange={(e) => onFile(e.target.files?.[0])} />
          </div>
        </div>

        <Tabs value={mode} onValueChange={setMode} className="mt-6">
          <TabsList className="bg-secondary/50">
            <TabsTrigger value="single"><Layers className="mr-2 h-3.5 w-3.5" /> Single</TabsTrigger>
            <TabsTrigger value="batch"><Files className="mr-2 h-3.5 w-3.5" /> Batch</TabsTrigger>
            <TabsTrigger value="cmyk"><Palette className="mr-2 h-3.5 w-3.5" /> Sim Process (CMYK)</TabsTrigger>
            <TabsTrigger value="mockup"><Shirt className="mr-2 h-3.5 w-3.5" /> Mockup</TabsTrigger>
          </TabsList>

          {/* SINGLE MODE */}
          <TabsContent value="single">
            <div className="mt-6 grid gap-6 lg:grid-cols-[300px_1fr]">
              <aside className="rounded-2xl border border-border bg-card p-5">
                <h3 className="display-font mb-3 flex items-center gap-2 text-sm font-semibold"><ShirtIcon className="h-4 w-4 text-[#4aa4ff]" /> Fabric</h3>
                <div className="grid grid-cols-3 gap-2">
                  {FABRICS.map((f) => (
                    <button key={f.id} onClick={() => setFabric(f.id)}
                      className={`rounded-lg border p-2 text-xs ${params.fabric === f.id ? "border-[#4aa4ff] bg-[#4aa4ff]/10 text-foreground" : "border-border text-muted-foreground hover:text-foreground"}`}
                    >{f.label}</button>
                  ))}
                </div>
                <h3 className="display-font mb-3 mt-6 text-sm font-semibold">Style</h3>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {HALFTONE_STYLES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}
                  </SelectContent>
                </Select>
                <div className="mt-6 space-y-5">
                  <div>
                    <div className="mb-1 flex items-center justify-between"><Label>LPI</Label><span className="mono text-xs text-muted-foreground">{params.lpi}</span></div>
                    <Slider min={10} max={80} step={1} value={[params.lpi]} onValueChange={([v]) => setParams((p) => ({ ...p, lpi: v }))} />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between"><Label>Angle</Label><span className="mono text-xs text-muted-foreground">{params.angle}°</span></div>
                    <Slider min={0} max={90} step={0.5} value={[params.angle]} onValueChange={([v]) => setParams((p) => ({ ...p, angle: v }))} />
                  </div>
                  <div>
                    <div className="mb-1 flex items-center justify-between"><Label>Underbase choke</Label><span className="mono text-xs text-muted-foreground">{params.underbaseChoke}px</span></div>
                    <Slider min={0} max={5} step={0.5} value={[params.underbaseChoke]} onValueChange={([v]) => setParams((p) => ({ ...p, underbaseChoke: v }))} />
                  </div>
                  <div className="flex items-center justify-between"><Label>Underbase</Label><Switch checked={params.underbase} onCheckedChange={(v) => setParams((p) => ({ ...p, underbase: v }))} /></div>
                  <div className="flex items-center justify-between"><Label>Invert</Label><Switch checked={params.invert} onCheckedChange={(v) => setParams((p) => ({ ...p, invert: v }))} /></div>
                  <div><Label>Foreground color</Label><Input type="color" value={params.fgColor} onChange={(e) => setParams((p) => ({ ...p, fgColor: e.target.value }))} className="mt-1 h-10 w-full cursor-pointer" /></div>
                </div>
                <div className="mt-6 space-y-2 border-t border-border pt-4">
                  <Button variant="outline" className="w-full gap-2" onClick={() => setParams({ ...DEFAULT_HALFTONE_PARAMS, ...fabricDefaults(params.fabric) })}><RotateCcw className="h-4 w-4" /> Reset</Button>
                  <Button className="w-full gap-2" variant="outline" onClick={saveProject} disabled={!img}><Save className="h-4 w-4" /> Save to Projects</Button>
                  <Button className="btn-ember w-full gap-2 font-semibold" onClick={runDtfOptimize} disabled={aiBusy || !img}>
                    {aiBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} AI DTF Optimize (Creator)
                  </Button>
                </div>
              </aside>

              <main>
                {!img ? (
                  <div onClick={() => fileRef.current?.click()}
                    onDragOver={(e) => e.preventDefault()}
                    onDrop={async (e) => { e.preventDefault(); await onFile(e.dataTransfer.files?.[0]); }}
                    className="flex min-h-[420px] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-card p-10 text-center hover:border-[#4aa4ff]/60">
                    <div className="inline-flex h-14 w-14 items-center justify-center rounded-2xl bg-secondary text-[#4aa4ff]"><ImageIcon className="h-6 w-6" /></div>
                    <div className="display-font text-lg font-semibold">Drop your artwork here</div>
                    <div className="text-sm text-muted-foreground">PNG (transparent), JPG or SVG · up to 20 MB</div>
                    <Button className="btn-ember mt-3 h-11 px-5 font-semibold">Choose a file</Button>
                  </div>
                ) : (
                  <Tabs defaultValue="color">
                    <TabsList className="bg-secondary/50">
                      <TabsTrigger value="color">Color layer</TabsTrigger>
                      <TabsTrigger value="underbase" disabled={!params.underbase}>Underbase</TabsTrigger>
                    </TabsList>
                    <TabsContent value="color">
                      <div className="rounded-2xl border border-border bg-white p-4">
                        <canvas ref={previewRef} className="mx-auto max-h-[560px] w-auto max-w-full rounded-lg" />
                      </div>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button className="btn-ember h-11 gap-2 font-semibold" onClick={exportColor} disabled={busy}><Download className="h-4 w-4" /> Download color PNG</Button>
                        <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> New file</Button>
                        <div className="ml-auto self-center text-xs text-muted-foreground">{usage?.pro ? "Pro · Unlimited exports" : `${usage?.remaining ?? 0} free exports left this month`}</div>
                      </div>
                    </TabsContent>
                    <TabsContent value="underbase">
                      <div className="rounded-2xl border border-border bg-white p-4">
                        <canvas ref={underbaseRef} className="mx-auto max-h-[560px] w-auto max-w-full rounded-lg" />
                      </div>
                      <div className="mt-4 flex flex-wrap gap-3">
                        <Button className="btn-ember h-11 gap-2 font-semibold" onClick={exportUnderbase} disabled={busy}><Download className="h-4 w-4" /> Download underbase PNG</Button>
                      </div>
                    </TabsContent>
                  </Tabs>
                )}
                <div className="mt-6 flex items-center gap-2 rounded-xl border border-border bg-card p-4 text-xs text-muted-foreground">
                  <Flame className="h-4 w-4 text-[#4aa4ff]" /> Halftone processing runs entirely in your browser — your art never leaves your device.
                </div>
              </main>
            </div>
          </TabsContent>

          {/* BATCH MODE */}
          <TabsContent value="batch">
            <div className="mt-6 rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-center gap-3">
                <label className="cursor-pointer">
                  <input type="file" accept="image/*" multiple hidden onChange={(e) => onBatchFiles(e.target.files)} />
                  <Button variant="outline" className="gap-2" asChild><span><Upload className="h-4 w-4" /> Select up to 20 files</span></Button>
                </label>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{HALFTONE_STYLES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
                <Button className="btn-ember h-10 gap-2 font-semibold" onClick={runBatch} disabled={busy || !batchFiles.length}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Run halftone on all
                </Button>
                <Button variant="outline" className="ml-auto gap-2" onClick={downloadBatch} disabled={!batchFiles.some((b) => b.resultCanvas)}>
                  <Package className="h-4 w-4" /> Download all as ZIP
                </Button>
              </div>
              <div className="mt-5 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
                {batchFiles.map((b, i) => (
                  <div key={i} className="rounded-xl border border-border bg-background p-3">
                    <div className="mono truncate text-[11px] text-muted-foreground">{b.name}</div>
                    <div className="mt-2 aspect-square overflow-hidden rounded-md bg-white">
                      {b.resultCanvas ? <BatchThumb canvas={b.resultCanvas} /> : <BatchThumbImg img={b.imgEl} />}
                    </div>
                    <div className="mt-1 text-[10px]">{b.done ? <span className="text-[#4aa4ff]">✓ Done</span> : <span className="text-muted-foreground">pending</span>}</div>
                  </div>
                ))}
                {!batchFiles.length && <div className="col-span-full py-10 text-center text-sm text-muted-foreground">No files selected yet. Pick up to 20 images.</div>}
              </div>
            </div>
          </TabsContent>

          {/* CMYK MODE */}
          <TabsContent value="cmyk">
            <div className="mt-6 rounded-2xl border border-border bg-card p-6">
              <div className="flex flex-wrap items-center gap-3">
                <Button variant="outline" onClick={() => fileRef.current?.click()} className="gap-2"><Upload className="h-4 w-4" /> Upload image</Button>
                <Select value={style} onValueChange={setStyle}>
                  <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
                  <SelectContent>{HALFTONE_STYLES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                </Select>
                <Button className="btn-ember h-10 gap-2 font-semibold" onClick={runCmyk} disabled={busy || !img}>
                  {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Palette className="h-4 w-4" />} Separate into CMYK
                </Button>
              </div>
              <p className="mt-2 text-xs text-muted-foreground">Splits your artwork into 4 press-ready channels (Cyan 15°, Magenta 75°, Yellow 0°, Black 45°) — each halftoned separately for simulated process printing.</p>

              <div className="mt-6 grid gap-4 lg:grid-cols-4">
                {["C", "M", "Y", "K"].map((ch) => (
                  <div key={ch} className="rounded-xl border border-border bg-background p-3">
                    <div className="flex items-center justify-between">
                      <div className="mono text-[11px] uppercase tracking-widest text-muted-foreground">Channel</div>
                      <div className="mono text-[11px] font-bold">{ch}</div>
                    </div>
                    <div className="mt-2 rounded-md bg-white p-2">
                      <canvas ref={cmykRefs[ch]} className="max-h-[240px] w-full object-contain" />
                    </div>
                    <Button variant="outline" size="sm" className="mt-2 w-full gap-2 text-xs" disabled={!cmykReady} onClick={() => downloadCmyk(ch)}>
                      <Download className="h-3.5 w-3.5" /> Download {ch}
                    </Button>
                  </div>
                ))}
              </div>

              {cmykReady && (
                <div className="mt-6 rounded-xl border border-border bg-background p-4">
                  <div className="mono text-[10px] uppercase tracking-widest text-muted-foreground">Composite preview</div>
                  <div className="mt-2 rounded-md bg-white p-3">
                    <canvas ref={cmykRefs.COMPOSITE} className="mx-auto max-h-[480px] w-auto max-w-full" />
                  </div>
                </div>
              )}
            </div>
          </TabsContent>

          {/* MOCKUP MODE */}
          <TabsContent value="mockup">
            <div className="mt-6 grid gap-6 lg:grid-cols-[300px_1fr]">
              <aside className="rounded-2xl border border-border bg-card p-5 space-y-4">
                <div>
                  <Label>T-shirt color</Label>
                  <div className="mt-2 grid grid-cols-6 gap-2">
                    {["#111827", "#374151", "#6b7280", "#f5f5f4", "#dc2626", "#2563eb", "#059669", "#facc15", "#7c3aed", "#0f766e", "#c2410c", "#ec4899"].map((c) => (
                      <button key={c} onClick={() => setTeeColor(c)} className={`h-8 w-8 rounded-full border-2 ${teeColor === c ? "border-[#4aa4ff]" : "border-transparent"}`} style={{ background: c }} />
                    ))}
                  </div>
                </div>
                <div>
                  <Label>Halftone style</Label>
                  <Select value={style} onValueChange={setStyle}>
                    <SelectTrigger className="mt-1"><SelectValue /></SelectTrigger>
                    <SelectContent>{HALFTONE_STYLES.map((s) => (<SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>))}</SelectContent>
                  </Select>
                </div>
                <p className="text-xs text-muted-foreground">Instant on-shirt preview — grab this to show clients or post to socials.</p>
                <Button className="btn-ember w-full gap-2 font-semibold" onClick={() => downloadCanvas(mockupRef.current, `mockup_${style}.png`)} disabled={!img}>
                  <Download className="h-4 w-4" /> Download mockup PNG
                </Button>
              </aside>
              <main>
                {!img ? (
                  <div onClick={() => fileRef.current?.click()} className="flex min-h-[420px] cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed border-border bg-card p-10 text-center hover:border-[#4aa4ff]/60">
                    <ImageIcon className="h-8 w-8 text-[#4aa4ff]" />
                    <div className="display-font">Upload artwork to place on a shirt</div>
                    <Button className="btn-ember h-11">Choose a file</Button>
                  </div>
                ) : (
                  <div className="rounded-2xl border border-border bg-[#f8fafc] p-4">
                    <canvas ref={mockupRef} className="mx-auto max-h-[600px] w-auto max-w-full rounded-lg" />
                  </div>
                )}
              </main>
            </div>
          </TabsContent>
        </Tabs>
      </section>
      <Footer />
    </div>
  );
}

function BatchThumb({ canvas }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !canvas) return;
    const c = ref.current;
    c.width = canvas.width; c.height = canvas.height;
    c.getContext("2d").drawImage(canvas, 0, 0);
  }, [canvas]);
  return <canvas ref={ref} className="h-full w-full object-contain" />;
}
function BatchThumbImg({ img }) {
  const ref = React.useRef(null);
  React.useEffect(() => {
    if (!ref.current || !img) return;
    const c = ref.current;
    const w = img.naturalWidth || img.width;
    const h = img.naturalHeight || img.height;
    c.width = w; c.height = h;
    c.getContext("2d").drawImage(img, 0, 0);
  }, [img]);
  return <canvas ref={ref} className="h-full w-full object-contain" />;
}
